import { useState } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useI18n } from '../../hooks/useI18n';

export function WhitelistModal({ open, onClose, whitelist = [], onAdd, onRemove, pushToast }) {
  const { t } = useI18n();
  const [newAddress, setNewAddress] = useState('');
  const [adding, setAdding] = useState(false);

  if (!open) return null;

  const handleAdd = async () => {
    if (!newAddress) {
      pushToast?.(t('wallet.enterAddress'), 'warning');
      return;
    }
    if (!newAddress.startsWith('0x') || newAddress.length !== 42) {
      pushToast?.(t('wallet.invalidAddress'), 'error');
      return;
    }

    setAdding(true);
    try {
      await onAdd?.(newAddress);
      setNewAddress('');
    } catch (e) {
      // Error handled by parent
    }
    setAdding(false);
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('wallet.manageWhitelist')}</h3>
            <p className="text-sm text-gray-400">{t('wallet.whitelistDesc')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Add New Address */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="0x..."
              className="flex-1 bg-surface border border-glass-border rounded-xl px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all font-mono text-sm"
            />
            <button
              onClick={handleAdd}
              disabled={adding}
              className="px-4 py-2.5 rounded-xl bg-accent hover:bg-accent-soft text-white text-sm font-medium transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              {adding ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <FiPlus size={16} />
              )}
              {t('wallet.add')}
            </button>
          </div>

          {/* Whitelist */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {whitelist.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{t('wallet.noWhitelist')}</div>
            ) : (
              whitelist.map((address) => (
                <div
                  key={address}
                  className="flex items-center justify-between p-3 bg-surface rounded-xl border border-glass-border group"
                >
                  <code className="text-sm font-mono text-gray-300 truncate flex-1">
                    {address.slice(0, 10)}...{address.slice(-8)}
                  </code>
                  <button
                    onClick={() => onRemove?.(address)}
                    className="p-2 rounded-lg text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('wallet.remove')}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-glass-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-surface-highlight hover:bg-surface text-white text-sm font-medium transition-colors"
          >
            {t('wallet.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
